import React, { useState, useEffect } from "react";
import {
  Button,
  Dialog,
  DialogContent,
  TextField,
  CircularProgress,
  Snackbar,
  Alert,
  Grid,
} from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { Timestamp } from "firebase/firestore"; // Import Timestamp
import useFirestore from "../../../../config/fireBaseHook"; // Import useFirestore
import useSendSchoolNotification from "../../../../config/sendSchoolNotificationHook"; // Import notification hook
import ModalDialogTitle from "../../../common/ModalDialogTitle";
import ModalActions from "../../../common/ModalActions";

const StudentDropForm = ({ schoolId, pathwayEmail, opEmail }) => {
  // Use Firestore hook to add data to 'StudentDropRequest' collection
  const { addData: addFirestoreData } = useFirestore("StudentDropRequest");
  // Use notification hook
  const {
    sendNotification,
    loading: notificationLoading,
    error: notificationError,
  } = useSendSchoolNotification();

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [formData, setFormData] = useState({
    schoolName: "",
    studentName: "",
    dob: "",
    courseName: "",
    term: "",
    fromName: "",
    fromEmail: "",
    comments: "",
    pathwayEmail: pathwayEmail || "", // Initialize with prop values
    opEmail: opEmail || "",
    schoolId: schoolId,
  });

  useEffect(() => {
    setFormData((prevData) => ({
      ...prevData,
      pathwayEmail: pathwayEmail || "", // Update if props change
      opEmail: opEmail || "",
    }));
  }, [pathwayEmail, opEmail]); // Add to dependency array

  const handleClickOpen = () => {
    setOpen(true);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Basic validation
    if (!formData.fromName || formData.fromName.trim() === "") {
      setSnackbarMessage("From Name is required.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
    if (!formData.schoolName || formData.schoolName.trim() === "") {
      setSnackbarMessage("School Name is required.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }
    if (!formData.studentName || formData.studentName.trim() === "") {
      setSnackbarMessage("Student Name is required.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    setLoading(true);
    setSnackbarMessage(""); // Clear previous messages

    const emailBodyContent = [
      `<strong>School Name:</strong> ${formData.schoolName || "N/A"}`,
      `<strong>From Name:</strong> ${formData.fromName || "N/A"}`,
      `<strong>From Email:</strong> ${formData.fromEmail || "N/A"}`,
      `<strong>Student Name:</strong> ${formData.studentName || "N/A"}`,
      `<strong>Date of Birth:</strong> ${formData.dob || "N/A"}`,
      `<strong>Course Name:</strong> ${formData.courseName || "N/A"}`,
      `<strong>Term:</strong> ${formData.term || "N/A"}`,
      `<strong>Comments:</strong> ${formData.comments || "N/A"}`,
    ].join("<br />");

    // Prepare data for Firestore, including the timestamp
    const dataForFirestore = {
      ...formData,
      created_at: Timestamp.now(), // Use created_at for consistency
    };

    // Prepare data for the notification
    const dataForNotification = {
      pathwayEmail:
        formData.pathwayEmail || pathwayEmail || "ccplus@sinclair.edu",
      opEmail: formData.opEmail || opEmail || "ccplus@sinclair.edu",
      schoolId: formData.schoolId || schoolId || "1",
      fromName: formData.fromName.trim(),
      schoolName: formData.schoolName.trim(),
      emailBodyContent: emailBodyContent,
    };

    try {
      // 1. Add data to Firestore
      await addFirestoreData(dataForFirestore);

      // 2. Send notification email
      await sendNotification(
        dataForNotification,
        "Student Drop Request",
        "StudentDrop"
      );

      setLoading(false);
      setSnackbarMessage(
        "Student drop request submitted and notification sent successfully!"
      );
      setSnackbarSeverity("success");
      setSnackbarOpen(true);
      handleClose();
    } catch (error) {
      console.error("Error submitting form:", error);
      const message = error.message || "An error occurred during submission.";
      setLoading(false);
      setSnackbarMessage(`Error submitting drop request: ${message}`);
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
    }
  };

  const handleClose = () => {
    setOpen(false);
    setLoading(false);
    setFormData({
      schoolName: "",
      studentName: "",
      dob: "",
      courseName: "",
      term: "",
      fromName: "",
      fromEmail: "",
      comments: "",
      schoolId: schoolId,
      pathwayEmail: pathwayEmail || "", // Keep emails on close
      opEmail: opEmail || "", // Keep emails on close
    });
  };

  return (
    <div>
      <Button
        variant="contained"
        color="secondary"
        startIcon={<FontAwesomeIcon icon={faPlus} />}
        onClick={handleClickOpen}
        sx={{ backgroundColor: "purple", color: "white" }}
      >
        Student Drop Form
      </Button>
      <Dialog open={open} onClose={handleClose}>
        <ModalDialogTitle onClose={handleClose}>
          Student Drop Form
        </ModalDialogTitle>
        <DialogContent>
          {loading ? (
            <CircularProgress />
          ) : (
            <form onSubmit={handleSubmit}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="dense"
                    label="School Name"
                    name="schoolName"
                    fullWidth
                    required
                    value={formData.schoolName}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="dense"
                    label="Student Name"
                    name="studentName"
                    fullWidth
                    required
                    value={formData.studentName}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="dense"
                    label="Date of Birth"
                    type="date"
                    name="dob"
                    fullWidth
                    value={formData.dob}
                    onChange={handleChange}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="dense"
                    label="Course Name"
                    name="courseName"
                    fullWidth
                    value={formData.courseName}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="dense"
                    label="Term"
                    name="term"
                    fullWidth
                    value={formData.term}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="dense"
                    label="From Name"
                    name="fromName"
                    fullWidth
                    required
                    value={formData.fromName}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    margin="dense"
                    label="From Email"
                    name="fromEmail"
                    fullWidth
                    value={formData.fromEmail}
                    onChange={handleChange}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    margin="dense"
                    label="Comments"
                    name="comments"
                    fullWidth
                    multiline
                    rows={4}
                    value={formData.comments}
                    onChange={handleChange}
                  />
                </Grid>
              </Grid>
            </form>
          )}
        </DialogContent>
        <ModalActions onSave={handleSubmit} saving={loading} saveLabel="Submit" />
      </Dialog>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{ width: "100%" }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{
            backgroundColor: snackbarSeverity === "success" ? "black" : "red",
            color: "white",
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default StudentDropForm;
